/**
 * Service for managing academic calendars in Firestore
 * Provides CRUD operations for school-specific academic calendars
 * Handles terms, semesters, holidays, and school year configurations
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase.js';

class AcademicCalendarService {
  constructor() {
    this.collectionName = 'academicCalendars';
    this.termsCollectionName = 'academicTerms';
    this.holidaysCollectionName = 'academicHolidays';
    this.cache = new Map(); // schoolId -> calendar cache
    this.cacheTimestamp = new Map();
    this.cacheDuration = 10 * 60 * 1000; // 10 minutes
  }

  /**
   * Get collection reference for a school
   * @param {string} schoolId - The school ID
   */
  getCollectionRef(schoolId) {
    return collection(db, 'schools', schoolId, this.collectionName);
  }

  /**
   * Get terms collection reference for a school
   * @param {string} schoolId - The school ID
   */
  getTermsCollectionRef(schoolId) {
    return collection(db, 'schools', schoolId, this.termsCollectionName);
  }

  /**
   * Get holidays collection reference for a school
   * @param {string} schoolId - The school ID
   */
  getHolidaysCollectionRef(schoolId) {
    return collection(db, 'schools', schoolId, this.holidaysCollectionName);
  }

  /**
   * Check if cache is valid for a school
   * @param {string} schoolId - The school ID
   */
  isCacheValid(schoolId) {
    const timestamp = this.cacheTimestamp.get(schoolId);
    if (!timestamp) return false;
    return Date.now() - timestamp < this.cacheDuration;
  }

  /**
   * Clear cache for a school or all schools
   * @param {string} schoolId - The school ID (optional)
   */
  clearCache(schoolId = null) {
    if (schoolId) {
      this.cache.delete(schoolId);
      this.cacheTimestamp.delete(schoolId);
    } else {
      this.cache.clear();
      this.cacheTimestamp.clear();
    }
  }

  /**
   * Get academic calendar for a school (with caching)
   * @param {string} schoolId - The school ID
   * @param {number} academicYear - The academic year (optional)
   * @returns {Promise<Object>} Academic calendar object
   */
  async getCalendar(schoolId, academicYear = null) {
    const cacheKey = `${schoolId}-${academicYear || 'current'}`;
    
    // Return cached data if valid
    if (this.isCacheValid(schoolId) && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      // Get the academic calendar
      const calendarQuery = academicYear 
        ? query(this.getCollectionRef(schoolId), where('academicYear', '==', academicYear))
        : query(this.getCollectionRef(schoolId), where('isActive', '==', true));
      
      const calendarSnapshot = await getDocs(calendarQuery);
      let calendar = null;

      if (!calendarSnapshot.empty) {
        const calendarDoc = calendarSnapshot.docs[0];
        calendar = {
          id: calendarDoc.id,
          ...calendarDoc.data()
        };
      } else {
        // Create default calendar if none exists
        calendar = await this.createDefaultCalendar(schoolId, academicYear);
      }

      // Get terms for this calendar
      const termsSnapshot = await getDocs(
        query(this.getTermsCollectionRef(schoolId), 
              where('calendarId', '==', calendar.id),
              orderBy('startDate'))
      );

      const terms = termsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get holidays for this calendar
      const holidaysSnapshot = await getDocs(
        query(this.getHolidaysCollectionRef(schoolId),
              where('calendarId', '==', calendar.id),
              orderBy('startDate'))
      );

      const holidays = holidaysSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Build complete calendar object
      const completeCalendar = {
        ...calendar,
        terms,
        holidays
      };

      // Update cache
      this.cache.set(cacheKey, completeCalendar);
      this.cacheTimestamp.set(schoolId, Date.now());

      return completeCalendar;
    } catch (error) {
      console.error('Error fetching academic calendar:', error);
      throw error;
    }
  }

  /**
   * Create a new academic calendar
   * @param {string} schoolId - The school ID
   * @param {Object} data - Calendar data
   * @returns {Promise<string>} New document ID
   */
  async createCalendar(schoolId, data) {
    try {
      const docData = {
        ...data,
        isActive: data.isActive !== undefined ? data.isActive : true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(this.getCollectionRef(schoolId), docData);
      this.clearCache(schoolId);
      return docRef.id;
    } catch (error) {
      console.error('Error creating academic calendar:', error);
      throw error;
    }
  }

  /**
   * Update an academic calendar
   * @param {string} schoolId - The school ID
   * @param {string} calendarId - The calendar ID
   * @param {Object} data - Updated data
   * @returns {Promise<void>}
   */
  async updateCalendar(schoolId, calendarId, data) {
    try {
      const docRef = doc(this.getCollectionRef(schoolId), calendarId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
      this.clearCache(schoolId);
    } catch (error) {
      console.error('Error updating academic calendar:', error);
      throw error;
    }
  }

  /**
   * Delete an academic calendar
   * @param {string} schoolId - The school ID
   * @param {string} calendarId - The calendar ID
   * @returns {Promise<void>}
   */
  async deleteCalendar(schoolId, calendarId) {
    try {
      const docRef = doc(this.getCollectionRef(schoolId), calendarId);
      await deleteDoc(docRef);
      this.clearCache(schoolId);
    } catch (error) {
      console.error('Error deleting academic calendar:', error);
      throw error;
    }
  }

  /**
   * Create academic terms for a calendar
   * @param {string} schoolId - The school ID
   * @param {string} calendarId - The calendar ID
   * @param {Array} terms - Array of term objects
   * @returns {Promise<Array>} Array of term IDs
   */
  async createTerms(schoolId, calendarId, terms) {
    try {
      const batch = writeBatch(db);
      const termIds = [];

      terms.forEach((termData) => {
        const docRef = doc(this.getTermsCollectionRef(schoolId));
        batch.set(docRef, {
          ...termData,
          calendarId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        termIds.push(docRef.id);
      });

      await batch.commit();
      this.clearCache(schoolId);
      return termIds;
    } catch (error) {
      console.error('Error creating academic terms:', error);
      throw error;
    }
  }

  /**
   * Create holidays for a calendar
   * @param {string} schoolId - The school ID
   * @param {string} calendarId - The calendar ID
   * @param {Array} holidays - Array of holiday objects
   * @returns {Promise<Array>} Array of holiday IDs
   */
  async createHolidays(schoolId, calendarId, holidays) {
    try {
      const batch = writeBatch(db);
      const holidayIds = [];

      holidays.forEach((holidayData) => {
        const docRef = doc(this.getHolidaysCollectionRef(schoolId));
        batch.set(docRef, {
          ...holidayData,
          calendarId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        holidayIds.push(docRef.id);
      });

      await batch.commit();
      this.clearCache(schoolId);
      return holidayIds;
    } catch (error) {
      console.error('Error creating academic holidays:', error);
      throw error;
    }
  }

  /**
   * Get current academic term for a date
   * @param {string} schoolId - The school ID
   * @param {Date} date - The date to check (defaults to today)
   * @returns {Promise<Object|null>} Current term or null
   */
  async getCurrentTerm(schoolId, date = new Date()) {
    try {
      const calendar = await this.getCalendar(schoolId);
      
      if (!calendar || !calendar.terms) {
        return null;
      }

      // Find term that contains the date
      const currentTerm = calendar.terms.find(term => {
        const startDate = term.startDate.toDate ? term.startDate.toDate() : new Date(term.startDate);
        const endDate = term.endDate.toDate ? term.endDate.toDate() : new Date(term.endDate);
        return date >= startDate && date <= endDate;
      });

      return currentTerm || null;
    } catch (error) {
      console.error('Error getting current term:', error);
      return null;
    }
  }

  /**
   * Check if a date is a school day (not weekend or holiday)
   * @param {string} schoolId - The school ID
   * @param {Date} date - The date to check
   * @returns {Promise<boolean>} True if it's a school day
   */
  async isSchoolDay(schoolId, date) {
    try {
      // Check if it's a weekend
      if (date.getDay() === 0 || date.getDay() === 6) { // Sunday = 0, Saturday = 6
        return false;
      }

      // Check if it's a holiday
      const calendar = await this.getCalendar(schoolId);
      
      if (!calendar || !calendar.holidays) {
        return true; // Assume school day if no holidays data
      }

      const isHoliday = calendar.holidays.some(holiday => {
        const startDate = holiday.startDate.toDate ? holiday.startDate.toDate() : new Date(holiday.startDate);
        const endDate = holiday.endDate.toDate ? holiday.endDate.toDate() : new Date(holiday.endDate);
        return date >= startDate && date <= endDate;
      });

      return !isHoliday;
    } catch (error) {
      console.error('Error checking school day:', error);
      return true; // Default to true on error
    }
  }

  /**
   * Get date range for academic timeframe
   * @param {string} schoolId - The school ID
   * @param {string} timeframe - The timeframe (academic-term, academic-semester, academic-year)
   * @param {number} timeCount - Number of timeframes to go back
   * @returns {Promise<Object|null>} Date range object or null if not applicable
   */
  async getAcademicDateRange(schoolId, timeframe, timeCount = 1) {
    try {
      const calendar = await this.getCalendar(schoolId);
      const currentDate = new Date();

      if (!calendar) {
        return null;
      }

      switch (timeframe) {
        case 'academic-term':
          const currentTerm = await this.getCurrentTerm(schoolId, currentDate);
          if (currentTerm) {
            const startDate = currentTerm.startDate.toDate ? currentTerm.startDate.toDate() : new Date(currentTerm.startDate);
            const endDate = currentTerm.endDate.toDate ? currentTerm.endDate.toDate() : new Date(currentTerm.endDate);
            return { start: startDate, end: endDate };
          }
          break;

        case 'academic-semester':
          // Find current semester (group of terms)
          const currentSemesterTerms = calendar.terms.filter(term => {
            const startDate = term.startDate.toDate ? term.startDate.toDate() : new Date(term.startDate);
            const endDate = term.endDate.toDate ? term.endDate.toDate() : new Date(term.endDate);
            return currentDate >= startDate && currentDate <= endDate && term.semester;
          });

          if (currentSemesterTerms.length > 0) {
            const semesterStart = new Date(Math.min(...currentSemesterTerms.map(t => 
              t.startDate.toDate ? t.startDate.toDate().getTime() : new Date(t.startDate).getTime()
            )));
            const semesterEnd = new Date(Math.max(...currentSemesterTerms.map(t => 
              t.endDate.toDate ? t.endDate.toDate().getTime() : new Date(t.endDate).getTime()
            )));
            return { start: semesterStart, end: semesterEnd };
          }
          break;

        case 'academic-year':
          // Use the academic year from calendar
          const academicYear = calendar.academicYear;
          const yearStartDate = new Date(academicYear, 7, 1); // August 1st
          const yearEndDate = new Date(academicYear + 1, 6, 30); // July 30th
          return { start: yearStartDate, end: yearEndDate };

        default:
          return null;
      }

      return null;
    } catch (error) {
      console.error('Error getting academic date range:', error);
      return null;
    }
  }

  /**
   * Create a single term
   * @param {string} schoolId - The school ID
   * @param {string} calendarId - The calendar ID
   * @param {Object} termData - Term data
   * @returns {Promise<string>} Term ID
   */
  async createTerm(schoolId, calendarId, termData) {
    try {
      const docData = {
        ...termData,
        calendarId,
        isActive: termData.isActive !== undefined ? termData.isActive : true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(this.getTermsCollectionRef(schoolId), docData);
      this.clearCache(schoolId);
      return docRef.id;
    } catch (error) {
      console.error('Error creating term:', error);
      throw error;
    }
  }

  /**
   * Update a term
   * @param {string} schoolId - The school ID
   * @param {string} termId - The term ID
   * @param {Object} termData - Updated term data
   * @returns {Promise<void>}
   */
  async updateTerm(schoolId, termId, termData) {
    try {
      const docRef = doc(this.getTermsCollectionRef(schoolId), termId);
      await updateDoc(docRef, {
        ...termData,
        updatedAt: serverTimestamp()
      });
      this.clearCache(schoolId);
    } catch (error) {
      console.error('Error updating term:', error);
      throw error;
    }
  }

  /**
   * Delete a term
   * @param {string} schoolId - The school ID
   * @param {string} termId - The term ID
   * @returns {Promise<void>}
   */
  async deleteTerm(schoolId, termId) {
    try {
      const docRef = doc(this.getTermsCollectionRef(schoolId), termId);
      await deleteDoc(docRef);
      this.clearCache(schoolId);
    } catch (error) {
      console.error('Error deleting term:', error);
      throw error;
    }
  }

  /**
   * Create a single holiday
   * @param {string} schoolId - The school ID
   * @param {string} calendarId - The calendar ID
   * @param {Object} holidayData - Holiday data
   * @returns {Promise<string>} Holiday ID
   */
  async createHoliday(schoolId, calendarId, holidayData) {
    try {
      const docData = {
        ...holidayData,
        calendarId,
        isActive: holidayData.isActive !== undefined ? holidayData.isActive : true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(this.getHolidaysCollectionRef(schoolId), docData);
      this.clearCache(schoolId);
      return docRef.id;
    } catch (error) {
      console.error('Error creating holiday:', error);
      throw error;
    }
  }

  /**
   * Update a holiday
   * @param {string} schoolId - The school ID
   * @param {string} holidayId - The holiday ID
   * @param {Object} holidayData - Updated holiday data
   * @returns {Promise<void>}
   */
  async updateHoliday(schoolId, holidayId, holidayData) {
    try {
      const docRef = doc(this.getHolidaysCollectionRef(schoolId), holidayId);
      await updateDoc(docRef, {
        ...holidayData,
        updatedAt: serverTimestamp()
      });
      this.clearCache(schoolId);
    } catch (error) {
      console.error('Error updating holiday:', error);
      throw error;
    }
  }

  /**
   * Delete a holiday
   * @param {string} schoolId - The school ID
   * @param {string} holidayId - The holiday ID
   * @returns {Promise<void>}
   */
  async deleteHoliday(schoolId, holidayId) {
    try {
      const docRef = doc(this.getHolidaysCollectionRef(schoolId), holidayId);
      await deleteDoc(docRef);
      this.clearCache(schoolId);
    } catch (error) {
      console.error('Error deleting holiday:', error);
      throw error;
    }
  }

  /**
   * Create default calendar for a school
   * @param {string} schoolId - The school ID
   * @param {number} academicYear - The academic year (optional)
   * @returns {Promise<Object>} Created calendar
   */
  async createDefaultCalendar(schoolId, academicYear = null) {
    try {
      const currentYear = new Date().getFullYear();
      const year = academicYear || currentYear;
      
      // Default academic year runs August 1 - July 31
      const calendarData = {
        academicYear: year,
        schoolYear: `${year}-${year + 1}`,
        startDate: new Date(year, 7, 1), // August 1st
        endDate: new Date(year + 1, 6, 31), // July 31st
        isActive: true,
        timezone: 'UTC', // Default to UTC, schools can update
        createdVia: 'auto-generation'
      };

      const calendarId = await this.createCalendar(schoolId, calendarData);

      // Create default terms (2 semesters, 4 terms total)
      const defaultTerms = [
        {
          name: 'Fall Semester - Term 1',
          type: 'term',
          semester: 1,
          startDate: new Date(year, 7, 1), // August 1st
          endDate: new Date(year, 9, 31), // October 31st
          isActive: true
        },
        {
          name: 'Fall Semester - Term 2',
          type: 'term',
          semester: 1,
          startDate: new Date(year, 10, 1), // November 1st
          endDate: new Date(year, 11, 20), // December 20th
          isActive: true
        },
        {
          name: 'Spring Semester - Term 3',
          type: 'term',
          semester: 2,
          startDate: new Date(year + 1, 0, 6), // January 6th
          endDate: new Date(year + 1, 2, 31), // March 31st
          isActive: true
        },
        {
          name: 'Spring Semester - Term 4',
          type: 'term',
          semester: 2,
          startDate: new Date(year + 1, 3, 1), // April 1st
          endDate: new Date(year + 1, 6, 30), // July 30th
          isActive: true
        }
      ];

      await this.createTerms(schoolId, calendarId, defaultTerms);

      // Create default holidays (major US holidays)
      const currentYearForHolidays = year;
      const defaultHolidays = [
        {
          name: 'Thanksgiving Break',
          type: 'holiday',
          startDate: new Date(currentYearForHolidays, 10, 23, 17), // Nov 23, 5pm
          endDate: new Date(currentYearForHolidays, 10, 27, 23), // Nov 27, 11pm
          isActive: true
        },
        {
          name: 'Winter Break',
          type: 'holiday',
          startDate: new Date(currentYearForHolidays, 11, 21, 17), // Dec 21, 5pm
          endDate: new Date(currentYearForHolidays + 1, 0, 6, 23), // Jan 6, 11pm
          isActive: true
        },
        {
          name: 'Spring Break',
          type: 'holiday',
          startDate: new Date(currentYearForHolidays + 1, 2, 15, 17), // Mar 15, 5pm
          endDate: new Date(currentYearForHolidays + 1, 2, 22, 23), // Mar 22, 11pm
          isActive: true
        }
      ];

      await this.createHolidays(schoolId, calendarId, defaultHolidays);

      // Return complete calendar
      return await this.getCalendar(schoolId, year);
    } catch (error) {
      console.error('Error creating default calendar:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const academicCalendarService = new AcademicCalendarService();
export default academicCalendarService;